/**
 * Template Quick-Edit Dialog
 *
 * Opens the full clinical-template editor (same form as the /templates page) as a
 * modal, straight from the dictation / conversation panel. Lets the clinician tweak
 * the whole template — including the AI directions (custom_prompt) — without leaving
 * the session, then saves back to /api/templates/{id}. The next summary generation
 * uses the updated template.
 */

import { ClinicalTemplateDialog } from '@/components/templates/ClinicalTemplateDialog';
import {
  type ClinicalTemplate,
  type ClinicalTemplateFormState,
  clinicalTemplatePayloadFromForm,
  fetchJson,
  formFromTemplate,
} from '@/components/templates/template-utils';
import { useToast } from '@/hooks/use-toast';
import { useMemo, useRef, useState } from 'react';

interface TemplateQuickEditDialogProps {
  /** The template to edit, already fetched with its full content. */
  template: ClinicalTemplate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the saved template so the caller can refresh cached copies. */
  onSaved?: (updated: ClinicalTemplate) => void;
}

export const TemplateQuickEditDialog = ({ template, open, onOpenChange, onSaved }: TemplateQuickEditDialogProps) => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // Track which template is open right now, so a slow save that resolves after
  // the user has reopened a different template does not close it / lose its draft.
  const activeTemplateIdRef = useRef<number | null>(template?.id ?? null);
  activeTemplateIdRef.current = template?.id ?? null;

  // Rebuild the form whenever a different (freshly fetched) template is opened.
  const form = useMemo(() => (template ? formFromTemplate(template) : null), [template]);

  if (!template || !form) {
    return null;
  }

  const handleSubmit = async (values: ClinicalTemplateFormState) => {
    const savingTemplateId = template.id;
    setIsSaving(true);
    try {
      const updated = await fetchJson<ClinicalTemplate>(`/api/templates/${savingTemplateId}`, {
        method: 'PUT',
        body: JSON.stringify(clinicalTemplatePayloadFromForm(values)),
      });
      onSaved?.(updated);
      // Only act on the modal if the user is still editing the template we saved.
      if (activeTemplateIdRef.current === savingTemplateId) {
        onOpenChange(false);
        toast({ title: 'Sniðmát vistað', description: 'Næsta samantekt notar uppfærða sniðmátið.' });
      }
    } catch (error) {
      console.error('[TemplateQuickEditDialog] Error saving template:', error);
      if (activeTemplateIdRef.current === savingTemplateId) {
        toast({
          title: 'Ekki tókst að vista sniðmát',
          description: 'Reyndu aftur eða breyttu því á sniðmátasíðunni.',
          variant: 'destructive',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ClinicalTemplateDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Breyta sniðmáti"
      description="Breyttu sniðmátinu — næsta samantekt notar uppfærðu útgáfuna."
      form={form}
      onSubmit={handleSubmit}
      isSaving={isSaving}
    />
  );
};
